import React, { useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from '../i18n';
import type { Meditation } from '../types';
import './MeditationCard.css';

interface MeditationCardProps {
  meditation: Meditation;
  showButton?: boolean;
  buttonText?: string;
}

export const MeditationCard: React.FC<MeditationCardProps> = ({ 
  meditation, 
  showButton = false,
  buttonText
}) => {
  const navigate = useNavigate();
  const videoRef = useRef<HTMLVideoElement>(null);
  const t = useTranslation();

  const defaultButtonText = buttonText || t.meditation.startMeditation;

  // Check if we should show cover image instead of video
  const hasCover = !!meditation.cover_url;

  // Доступ по подписке: медитация заблокирована, если требует подписку и
  // пользователь её не открыл (премиум даёт is_paid = true с бэкенда), либо
  // это старая платная за цветы и не оплачена.
  const subLocked = meditation.requires_subscription && !meditation.is_paid;
  const crystalLocked = !meditation.is_paid && meditation.cost > 0;
  const locked = subLocked || crystalLocked;

  useEffect(() => {
    // Only attach video event listeners if we're showing video (no cover)
    if (hasCover) return;
    
    const video = videoRef.current;
    if (!video) return;

    console.log('MeditationCard:', meditation.title, 'is_paid:', meditation.is_paid, 'cost:', meditation.cost);

    const blockPlayback = (e: Event) => {
      // Block playback for locked meditations (по подписке или за цветы).
      if (locked) {
        console.log('🚫 Blocking playback:', meditation.title);
        e.preventDefault();
        e.stopPropagation();
        video.pause();
        video.currentTime = 0;

        alert(subLocked
          ? t.meditation.requiresSubscription
          : t.meditation.costAlert.replace('{cost}', String(meditation.cost)));
        return false;
      }
    };

    const preventClick = (e: Event) => {
      if (locked) {
        e.preventDefault();
        e.stopPropagation();
        return false;
      }
    };

    video.addEventListener('play', blockPlayback);
    video.addEventListener('playing', blockPlayback);
    video.addEventListener('click', preventClick);
    
    return () => {
      video.removeEventListener('play', blockPlayback);
      video.removeEventListener('playing', blockPlayback);
      video.removeEventListener('click', preventClick);
    };
  }, [meditation, t, hasCover]);

  const getDifficultyLabel = (difficulty: string): string => {
    // Use translated label from API if available
    if (meditation.difficulty_label) {
      return meditation.difficulty_label;
    }
    // Fallback to frontend translations
    const difficultyMap: { [key: string]: string } = {
      beginner: t.meditation.beginner,
      intermediate: t.meditation.intermediate,
      advanced: t.meditation.advanced
    };
    return difficultyMap[difficulty] || difficulty;
  };

  const handleClick = () => {
    navigate(`/meditations/${meditation.id}`);
  };

  const handleButtonClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    navigate(`/meditations/${meditation.id}`);
  };

  return (
    <div className="meditation-card" onClick={handleClick}>
      <div className="meditation-media-preview">
        {hasCover ? (
          // Show cover image
          <img 
            src={meditation.cover_url} 
            alt={meditation.title}
            className="meditation-cover"
          />
        ) : (
          // Show video player
          <video 
            ref={videoRef}
            preload="metadata" 
            playsInline 
            muted
            crossOrigin="anonymous"
            controls={!locked}
            controlsList="nodownload noplaybackrate"
            disablePictureInPicture={locked}
            disableRemotePlayback={locked}
            onClick={(e) => e.stopPropagation()}
            style={{ pointerEvents: locked ? 'none' : 'auto' }}
          >
            <source src={meditation.video_url} type="video/mp4" />
          </video>
        )}

        {/* Locked overlay for locked meditations (подписка или цветы) */}
        {locked && (
          <div className="media-locked-overlay">
            <div className="media-locked-icon">🔒</div>
          </div>
        )}
        
        {/* Status badges */}
        <div className="meditation-badges">
          {meditation.is_started && !meditation.is_completed && (
            <span className="in-progress-badge">{t.meditation.inProgress}</span>
          )}
          {meditation.is_completed && (
            <span className="completed-badge">✓ {t.meditation.completed}</span>
          )}
        </div>
        
        {/* Subscription-only badge */}
        {subLocked && (
          <div className="meditation-cost-badge">
            🔒 {t.meditation.subscriptionOnly}
          </div>
        )}

        {/* Cost badge (для платных за цветы, не подписочных) */}
        {!subLocked && meditation.cost > 0 && !meditation.is_paid && (
          <div className="meditation-cost-badge">
            🌻 {meditation.cost}
          </div>
        )}
        {meditation.cost > 0 && meditation.is_paid && (
          <div className="meditation-paid-badge">
            💰 {t.meditation.paid}
          </div>
        )}
        {meditation.is_free && (
          <div className="meditation-free-badge">{t.meditation.free}</div>
        )}
        
        {/* Duration badge */}
        <div className="meditation-duration">
          {meditation.duration} {t.common.minutes}
        </div>
        
        {/* Audio indicator */}
        {meditation.audio_url && (
          <div className="meditation-audio-badge">
            🎧
          </div>
        )}
      </div>
      <div className="meditation-info">
        <h3 className="meditation-title">{meditation.title}</h3>
        <p className="meditation-description">{meditation.description}</p>
        <span className="difficulty-badge-inline">{getDifficultyLabel(meditation.difficulty)}</span>
        {showButton && (
          <button className="meditation-button" onClick={handleButtonClick}>
            {defaultButtonText}
          </button>
        )}
      </div>
    </div>
  );
};
